import {FC} from 'react'
import {useApolloClient} from '@apollo/client'
import {useDispatch} from 'react-redux'
import {useHistory, useLocation} from 'react-router-dom'
import {PaginatorWithResult} from 'jbc-front/components/SearchForm'
import {asyncError} from 'store/actions/asyncError'
import {EmployeesList} from '../EmployeesList'
import {
  CHECK_NEED_EMPLOYEE_UPDATE,
  CheckNeedEmployeeQueryResult,
  CheckNeedEmployeeQueryVariables,
  EMPLOYEES,
  Employees,
  EmployeesQueryVariables,
} from './query'
import {
  convertFormToSearchParams,
  convertSearchParamsToForm,
  convertSearchParamsToGraphQL,
  convertSearchParamsToQueryString,
  getSearchParamsFromLocation,
  hasConditions,
} from './search'
import {SearchForm, addOptions} from 'features/employees/SearchForm'
import {useAddedFields, makeAddedFields} from 'hooks/useAddedFields'
import {useDisplayEmployeeLimit} from 'hooks/useDisplayEmployeeLimit'
import {useQuery} from 'hooks/graphql'

interface ChooseEmployeeProps {
  procedureType: string
  nextAction?: string
}

export const ChooseEmployee: FC<ChooseEmployeeProps> = ({procedureType, nextAction = 'detail'}) => {
  const dispatch = useDispatch()
  const location = useLocation()
  const history = useHistory()
  const apolloClient = useApolloClient()
  const [limit] = useDisplayEmployeeLimit()
  const [addedFields] = useAddedFields()
  const searchParams = {...getSearchParamsFromLocation(location), limit}
  searchParams._added_fields = makeAddedFields(addedFields, searchParams, addOptions)

  const {data, previousData, loading} = useQuery<Employees, EmployeesQueryVariables>(EMPLOYEES, {
    variables: convertSearchParamsToGraphQL(searchParams, procedureType),
    fetchPolicy: 'network-only',
  })
  const totalCount =
    loading && previousData ? previousData.client.employees.totalCount : data?.client?.employees?.totalCount || 0

  const handleClick = async (employeeId: string) => {
    try {
      const {data} = await apolloClient.query<CheckNeedEmployeeQueryResult, CheckNeedEmployeeQueryVariables>({
        query: CHECK_NEED_EMPLOYEE_UPDATE,
        variables: {employeeId},
        fetchPolicy: 'network-only',
      })
      if (data.client.employee.needEmployeeUpdate) {
        history.push(`/employees/${employeeId}/edit?procedure_type=${procedureType}&next_action=${nextAction}`)
      } else {
        history.push(`/${procedureType}/${nextAction}?employee_id=${employeeId}`)
      }
    } catch (err) {
      dispatch(asyncError(err))
    }
  }

  return (
    <div>
      <div className="l-title-wrap">
        <h1 className="m-title-main">従業員を選択してください</h1>
      </div>
      <div className="l-wrap-xl">
        <SearchForm
          values={convertSearchParamsToForm(searchParams)}
          totalCount={totalCount}
          isLoading={loading}
          onSubmit={(values) => {
            const {_page, ...newSearchParams} = convertFormToSearchParams(values) // reset page
            return history.push({search: convertSearchParamsToQueryString(newSearchParams)})
          }}
          onClear={() => history.push({search: ''})}
          hasConditions={hasConditions}
          showJoinedStatusField={false}
        />
        {data?.client && (
          <EmployeesList
            employees={data.client.employees.list}
            currentSortType={searchParams.sort_type}
            onClickItem={handleClick}
            onChangeSortType={(sortType) => {
              history.push({
                search: convertSearchParamsToQueryString({...searchParams, sort_type: sortType, _page: '1'}),
              })
            }}
          />
        )}
        <PaginatorWithResult
          onClick={(page: number) =>
            history.push({search: convertSearchParamsToQueryString({...searchParams, _page: String(page)})})
          }
          limit={Number(limit)}
          count={totalCount}
          page={Number(searchParams._page)}
          isLoading={loading}
        />
      </div>
    </div>
  )
}
